'use client';

import React, { useState } from 'react';
import Button from '@/ui/components/shared/Button';
import { urlParameterCodes } from '@/constants/urlParams';
import { usePathname } from 'next/navigation';
import { useShallowRouter } from '@/ui/hooks/useShallowRouter';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { useSubnavData } from '@/ui/providers/subnavDataProvider';
import { ShapeContent } from '@/utils/crystallize-shape-helper';
import { ButtonVariants } from '../shared/Button/types/ButtonVariants';
import { parseSubnavData } from './utils';

export default function SubnavMobile() {
  const { subnavData } = useSubnavData();

  const currentPath = usePathname();
  const shallowRouter = useShallowRouter();
  const [isDropDownOpen, setIsDropDownOpen] = useState(false);

  // Ignore legacy shapes
  if (subnavData?.shape?.identifier !== 'subnav-v2') {
    return undefined;
  }

  const {
    'cta-items': ctaItems,
    'subnav-items': subnavItems,
    'mobile-dropdown-title': dropDownMobileTitle,
  } = parseSubnavData(subnavData as ShapeContent);

  const cleanedSubNavItems = subnavItems?.filter((item) => !item['hide-on-mobile']) || [];
  const cleanedCtaItems = ctaItems?.filter((item) => !item['hide-on-mobile']) || [];

  const openChooser = (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    e.preventDefault();

    shallowRouter.push(currentPath, { [urlParameterCodes.productDetail.chooserOpen]: true }, true);
  };

  // eslint-disable-next-line consistent-return
  return (
    subnavData && (
      <div className="fixed bottom-0 left-0 z-30 w-full bg-white shadow-nuki-blured lg:hidden">
        {isDropDownOpen && (
          <div className="flex flex-col gap-3 px-4 py-2 md:px-5">
            {/* Items buttons */}
            {cleanedSubNavItems.map((item, index) => {
              const url = item?.['internal-path']?.[0]?.path || item?.['link-url'];

              return (
                <Button
                  key={index}
                  type="button"
                  openInNewTab={item['open-in-new-tab']}
                  href={url}
                  variant={(item?.['variant-on-mobile']?.[0] as ButtonVariants) || 'primary'}
                  className="inline-flex h-11 items-center justify-center font-bold normal-case"
                  onClick={(e) => {
                    if (item['open-chooser'] && !url) {
                      openChooser(e as React.MouseEvent<HTMLButtonElement>);
                    }
                  }}
                >
                  {item?.text}
                </Button>
              );
            })}
          </div>
        )}

        <div className="flex w-full items-stretch justify-between gap-5">
          {/* Dropdown container */}
          <div className="flex items-center py-2 pl-4 md:pl-5">
            {cleanedSubNavItems.length > 0 && (
              <button
                type="button"
                className="inline-flex items-center gap-2 rounded-full border border-neutral-40 pl-4 font-medium"
                onClick={() => setIsDropDownOpen((prev) => !prev)}
              >
                {dropDownMobileTitle || subnavItems?.[0]?.text}
                <span
                  className={clsx(
                    'm-1.5 inline-flex h-auto w-8 items-center justify-center rounded-full bg-neutral-10 p-1.5',
                  )}
                >
                  <ChevronDownIcon
                    className={clsx('w-full duration-300', { 'rotate-180': isDropDownOpen })}
                  />
                </span>
              </button>
            )}
          </div>

          {/* Buttons container */}
          <div className="flex justify-end py-2 pr-4 md:pr-5">
            <div className="flex w-full justify-between gap-3 md:w-fit md:justify-start">
              {cleanedCtaItems.map((cta, index) => {
                const url = cta?.['internal-path']?.[0]?.path || cta?.['link-url'];

                return (
                  <Button
                    key={index}
                    type="button"
                    href={url}
                    openInNewTab={cta['open-in-new-tab']}
                    variant={cta.variant[0] as ButtonVariants}
                    onClick={(e) => {
                      if (cta['open-chooser'] && !url) {
                        openChooser(e as React.MouseEvent<HTMLButtonElement>);
                      }
                    }}
                    className="text-sm md:py-2 md:text-md md:normal-case"
                  >
                    {cta.text}
                  </Button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    )
  );
}
